import { useEffect, useState } from 'react';
import { getLanguage } from '../i18n/registry';

export const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'https://api.noteician.com').replace(/\/+$/, '');

// Display-only fallback. Checkout availability is exclusively confirmed by the API.
export function fallbackCatalog(currency) {
  const prices = currency === 'eur'
    ? [{ plan: 'individual', interval: 'monthly', currency: 'eur', unitAmount: 3900, taxBehavior: 'inclusive', available: false }]
    : [
      { plan: 'individual', interval: 'monthly', currency: 'gbp', unitAmount: 3000, taxBehavior: 'unspecified', available: false },
      { plan: 'individual', interval: 'yearly', currency: 'gbp', unitAmount: 32400, taxBehavior: 'unspecified', available: false },
    ];
  return { currency, enabled: false, prices, supportedCurrencies: ['gbp', 'eur'] };
}

export function validateCatalog(value, currency) {
  if (value?.currency !== currency || typeof value.enabled !== 'boolean' || !Array.isArray(value.prices)) return null;
  const prices = value.prices.filter(price =>
    price.currency === currency && ['individual', 'practice'].includes(price.plan) &&
    ['monthly', 'yearly'].includes(price.interval) && Number.isInteger(price.unitAmount) && price.unitAmount > 0 &&
    ['inclusive', 'exclusive', 'unspecified'].includes(price.taxBehavior) && typeof price.available === 'boolean'
  );
  if (!prices.length) return null;
  return { ...value, prices: prices.map(price => ({ ...price, available: value.enabled && price.available })) };
}

export function useCatalog(currency) {
  const [result, setResult] = useState(null);
  useEffect(() => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    setResult(null);
    fetch(`${API_BASE_URL}/api/billing/catalog?currency=${currency}`, { signal: controller.signal })
      .then(response => response.ok ? response.json() : Promise.reject(new Error('Catalogue unavailable')))
      .then(value => {
        const catalog = validateCatalog(value, currency);
        if (!controller.signal.aborted && catalog) setResult(catalog);
      })
      .catch(() => { /* The display fallback never enables purchases. */ })
      .finally(() => clearTimeout(timeout));
    return () => { clearTimeout(timeout); controller.abort(); };
  }, [currency]);
  return result?.currency === currency ? result : fallbackCatalog(currency);
}

export function formatPrice(price, locale) {
  return new Intl.NumberFormat(getLanguage(locale, 'marketing').formatLocale, {
    style: 'currency', currency: price.currency.toUpperCase(), maximumFractionDigits: price.unitAmount % 100 ? 2 : 0,
  }).format(price.unitAmount / 100);
}
